(() => {
  if (window.RavinAboutFashionLabLoaded) return;
  window.RavinAboutFashionLabLoaded = true;
  const instances = new WeakMap();
  const clamp = (n, a, b) => Math.max(a, Math.min(b, n));
  function initialize(root) {
    if (!root || instances.has(root)) return;
    const controller = new AbortController();
    const signal = controller.signal;
    const hero = root.querySelector('[data-rfo-hero]');
    const revealed = Array.from(root.querySelectorAll('[data-rfo-reveal]'));
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const pointerFine = window.matchMedia('(pointer: fine)');
    let lastFrame = 0;
    let revealObserver = null;
    let lastOpener = null;

    if ('IntersectionObserver' in window && !reduced.matches) {
      root.classList.add('js-enhanced');
      revealObserver = new IntersectionObserver((entries, observer) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        }
      }, { threshold: 0.09, rootMargin: '0px 0px 80px 0px' });
      revealed.forEach(el => revealObserver.observe(el));
    }

    function animateHero() {
      lastFrame = 0;
      if (!hero || reduced.matches) return;
      const r = hero.getBoundingClientRect();
      const relative = clamp((-r.top) / Math.max(r.height, 1), 0, 1);
      hero.style.setProperty('--rfo-hero-y', (-45 * relative).toFixed(1) + 'px');
    }
    function requestHero() {
      if (!lastFrame) lastFrame = requestAnimationFrame(animateHero);
    }
    window.addEventListener('scroll', requestHero, { passive: true, signal });
    window.addEventListener('resize', requestHero, { passive: true, signal });
    window.addEventListener('pageshow', requestHero, { signal });

    function openProfile(button) {
      const id = button.getAttribute('data-rfo-open');
      const dialog = id ? document.getElementById(id) : null;
      if (!dialog) return;
      lastOpener = button;
      try {
        if (!dialog.open) dialog.showModal();
      } catch (error) {
        dialog.setAttribute('open', '');
      }
      const close = dialog.querySelector('[data-rfo-close]');
      close?.focus({preventScroll:true});
    }

    root.addEventListener('click', event => {
      const opener = event.target.closest('[data-rfo-open]');
      if (opener && root.contains(opener)) {
        event.preventDefault();
        openProfile(opener);
        return;
      }
      const closer = event.target.closest('[data-rfo-close]');
      if (closer && root.contains(closer)) {
        closer.closest('dialog')?.close();
      }
    }, { signal });

    for (const dialog of root.querySelectorAll('dialog.rfo-profile')) {
      dialog.addEventListener('click', event => {
        if (event.target !== dialog) return;
        const rectangle = dialog.getBoundingClientRect();
        if (event.clientX < rectangle.left || event.clientX > rectangle.right ||
          event.clientY < rectangle.top || event.clientY > rectangle.bottom) {
          dialog.close();
        }
      }, { signal });
      dialog.addEventListener('close', () => {
        if (lastOpener && lastOpener.isConnected) lastOpener.focus({preventScroll:true});
      }, { signal });
    }

    for (const card of root.querySelectorAll('.rfo-founder__card')) {
      card.addEventListener('pointermove', event => {
        if (!pointerFine.matches || reduced.matches || window.innerWidth < 750) return;
        const bounds = card.getBoundingClientRect();
        const x = clamp((event.clientX - bounds.left) / bounds.width * 2 - 1, -1, 1);
        const y = clamp((event.clientY - bounds.top) / bounds.height * 2 - 1, -1, 1);
        card.style.setProperty('--rfo-rx', (-y * 4).toFixed(2) + 'deg');
        card.style.setProperty('--rfo-ry', (x * 6).toFixed(2) + 'deg');
      }, {passive:true, signal});
      card.addEventListener('pointerleave', () => {
        card.style.removeProperty('--rfo-rx');
        card.style.removeProperty('--rfo-ry');
      }, {signal});
    }

    if (reduced.addEventListener) {
      reduced.addEventListener('change', () => {
        if (reduced.matches) {
          root.classList.remove('js-enhanced');
          revealed.forEach(el => el.classList.add('is-visible'));
          hero?.style.removeProperty('--rfo-hero-y');
          revealObserver?.disconnect();
        }
      }, { signal });
    }
    const film = root.querySelector('[data-rfo-film]');
    let filmFrame = 0;
    let filmActive = true;
    let filmObserver = null;
    let filmResize = null;
    if (film) {
      const stage = film.querySelector('[data-rfo-stage]');
      const overlay = film.querySelector('[data-rfo-film-overlay]');
      const progressBar = film.querySelector('[data-rfo-progress]');
      const skip = film.querySelector('[data-rfo-skip]');
      const story = root.querySelector('.rfo-origin');
      const smooth = t => t * t * (3 - 2 * t);
      const range = (p, a, b) => smooth(clamp((p - a) / (b - a), 0, 1));
      const lerp = (a, b, t) => a + (b - a) * t;
      const set = (key, value) => film.style.setProperty('--film-' + key, value);
      function drawFilm() {
        filmFrame = 0;
        const motion = !reduced.matches;
        film.classList.toggle('is-ready', motion);
        const rect = film.getBoundingClientRect();
        const p = motion ? clamp(-rect.top / Math.max(1, film.offsetHeight - stage.offsetHeight), 0, 1) : 0;
        const a = range(p, .15, .35);
        const b = range(p, .43, .63);
        const split = range(p, .74, .95);
        const side = window.innerWidth < 750 ? 17 : 27;
        const other = 100 - side;
        set('left-x', lerp(lerp(side, 50, a), other, b).toFixed(3) + '%');
        set('center-x', lerp(50, side, a).toFixed(3) + '%');
        set('right-x', lerp(other, 50, b).toFixed(3) + '%');
        set('left-scale', lerp(lerp(.86, 1.06, a), .86, b).toFixed(4));
        set('center-scale', lerp(1.06, .86, a).toFixed(4));
        set('right-scale', lerp(.86, 1.06, b).toFixed(4));
        set('left-z', a > .5 && b < .5 ? 3 : 2);
        set('center-z', a > .5 ? 1 : 3);
        set('right-z', b > .5 ? 3 : 1);
        set('split', split.toFixed(5));
        set('copy', (1 - range(p, .68, .77)).toFixed(4));
        const pulse = Math.max(0, 1 - Math.abs(p - .25) / .025, 1 - Math.abs(p - .53) / .025);
        set('glitch', pulse.toFixed(3));
        set('jitter', (Math.sin(p * 780) * pulse * 7).toFixed(2) + 'px');
        progressBar.style.transform = 'scaleX(' + p.toFixed(4) + ')';
        overlay.style.visibility = p > .8 ? 'hidden' : 'visible';
        film.dataset.phase = p < .35 ? 'first-swap' : p < .63 ? 'second-swap' : p < .74 ? 'hold' : 'reveal';
      }
      function queueFilm() {
        if (!filmFrame && filmActive) filmFrame = requestAnimationFrame(drawFilm);
      }
      function resizeFilm() { filmActive = true; queueFilm(); }
      window.addEventListener('scroll', queueFilm, { passive: true, signal });
      window.addEventListener('resize', resizeFilm, { passive: true, signal });
      window.addEventListener('pageshow', resizeFilm, { signal });
      reduced.addEventListener('change', resizeFilm, { signal });
      if ('ResizeObserver' in window) {
        filmResize = new ResizeObserver(queueFilm);
        filmResize.observe(stage);
      }
      if ('IntersectionObserver' in window) {
        filmObserver = new IntersectionObserver(entries => {
          filmActive = entries[0].isIntersecting;
          if (filmActive) queueFilm();
        }, { rootMargin: '150px' });
        filmObserver.observe(film);
      }
      skip.addEventListener('click', event => {
        if (!story) return;
        event.preventDefault();
        story.tabIndex = -1;
        story.scrollIntoView({ behavior: 'instant', block: 'start' });
        story.focus({ preventScroll: true });
      }, { signal });
      drawFilm();
    }

    requestHero();
    instances.set(root, () => {
      controller.abort();
      filmObserver?.disconnect();
      filmResize?.disconnect();
      if (filmFrame) cancelAnimationFrame(filmFrame);
      revealObserver?.disconnect();
      if (lastFrame) cancelAnimationFrame(lastFrame);
      for (const dialog of root.querySelectorAll('dialog[open]')) dialog.close();
      instances.delete(root);
    });
  }
  function mount(scope) {
    if (scope.matches?.('[data-rfo-root]')) initialize(scope);
    scope.querySelectorAll?.('[data-rfo-root]').forEach(initialize);
  }
  function unmount(scope) {
    const all = scope.matches?.('[data-rfo-root]') ? [scope] : Array.from(scope.querySelectorAll?.('[data-rfo-root]') || []);
    all.forEach(root => instances.get(root)?.());
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount(document), {once:true});
  else mount(document);
  document.addEventListener('shopify:section:load', e => mount(e.target));
  document.addEventListener('shopify:section:unload', e => unmount(e.target));
})();
